import React, { useState } from 'react';
import { FaShoppingCart, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { addToCart, getCart, updateCartItem, removeCartItem } from '../utils/api';
import ResponseDisplay from './ResponseDisplay';
import LoadingSpinner from './LoadingSpinner';
import { useCart } from './CartContext';
import toast from 'react-hot-toast';

const CartForm = () => {
  const [formData, setFormData] = useState({
    customerId: '',
    bookId: '',
    quantity: '',
  });
  const [errors, setErrors] = useState({});
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showSampleData, setShowSampleData] = useState(false);
  const { addItem, updateItem, removeItem } = useCart();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customerId) newErrors.customerId = 'Customer ID is required';
    if (!formData.bookId) newErrors.bookId = 'Book ID is required';
    if (!formData.quantity || formData.quantity <= 0) newErrors.quantity = 'Quantity must be greater than 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const executeAction = async (action) => {
    if (!validateForm()) return;
    setLoading(true);
    setLoadingMessage(`${action.charAt(0).toUpperCase() + action.slice(1)} cart, please wait... The backend may take up few seconds to wake up.`);

    try {
      let res;
      switch (action) {
        case 'add':
          res = await addToCart(formData.customerId, {
            bookId: formData.bookId,
            quantity: parseInt(formData.quantity),
          });
          addItem({
            customerId: formData.customerId,
            bookId: formData.bookId,
            quantity: parseInt(formData.quantity),
            title: res.data.title || 'Book',
          });
          setShowModal(true);
          break;
        case 'get':
          res = await getCart(formData.customerId);
          break;
        case 'update':
          res = await updateCartItem(formData.customerId, formData.bookId, {
            quantity: parseInt(formData.quantity),
          });
          updateItem(formData.customerId, formData.bookId, parseInt(formData.quantity));
          break;
        case 'remove':
          await removeCartItem(formData.customerId, formData.bookId);
          res = { data: { message: 'Item removed from cart successfully' } };
          removeItem(formData.customerId, formData.bookId);
          break;
        default:
          throw new Error('Invalid action');
      }
      setResponse(res.data);
      setError(null);
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} successful!`);
    } catch (err) {
      setError(err.response?.data || { error: `Failed to ${action} cart item` });
      setResponse(null);
      toast.error(`Failed to ${action} cart item`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <div className="max-w-4xl p-8 mx-auto mt-24 mb-12 transition-all duration-300 bg-white shadow-xl rounded-2xl">
      {loading && <LoadingSpinner message={loadingMessage} />}
      <h2 className="mb-6 text-3xl font-bold text-blue-600">Manage Your Cart</h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {[
          { name: 'customerId', label: 'Customer ID', placeholder: 'Enter Customer ID', type: 'text' },
          { name: 'bookId', label: 'Book ID', placeholder: 'Enter Book ID', type: 'text' },
          { name: 'quantity', label: 'Quantity', placeholder: 'Enter Quantity', type: 'number', min: 1 },
        ].map((field) => (
          <div key={field.name} className="relative">
            <label className="block mb-1 text-sm font-medium text-gray-700">{field.label}</label>
            <input
              type={field.type}
              name={field.name}
              placeholder={field.placeholder}
              value={formData[field.name]}
              onChange={handleChange}
              min={field.min}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${errors[field.name] ? 'border-red-500' : 'border-gray-300'}`}
              disabled={loading}
              aria-describedby={`${field.name}-error`}
            />
            {errors[field.name] && (
              <p id={`${field.name}-error`} className="mt-1 text-xs text-red-500">{errors[field.name]}</p>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 mt-8">
        <button
          onClick={() => executeAction('add')}
          disabled={loading}
          className={`flex items-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          aria-label="Add to Cart"
        >
          <FaShoppingCart className="mr-2" />
          {loading ? 'Adding...' : 'Add to Cart'}
        </button>
        {[
          { action: 'get', label: 'View Cart', color: 'bg-green-500 hover:bg-green-600' },
          { action: 'update', label: 'Update Item', color: 'bg-orange-500 hover:bg-orange-600' },
          { action: 'remove', label: 'Remove Item', color: 'bg-red-500 hover:bg-red-600' },
        ].map(({ action, label, color }) => (
          <button
            key={action}
            onClick={() => executeAction(action)}
            disabled={loading}
            className={`${color} text-white px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={label}
          >
            {loading ? 'Processing...' : label}
          </button>
        ))}
      </div>
      <div className="mt-8">
        <button
          onClick={() => setShowSampleData(!showSampleData)}
          className="flex items-center font-semibold text-blue-600 transition-all hover:text-blue-800"
          aria-expanded={showSampleData}
          aria-controls="sample-data"
        >
          {showSampleData ? <FaChevronUp className="mr-2" /> : <FaChevronDown className="mr-2" />}
          Sample Data
        </button>
        {showSampleData && (
          <div id="sample-data" className="p-6 mt-4 bg-gray-50 rounded-xl animate-fade-in">
            <h3 className="mb-4 text-lg font-semibold text-gray-800">Sample Cart Data</h3>
            <pre className="p-4 overflow-auto font-mono text-sm text-gray-800 bg-white rounded-lg">
              {JSON.stringify(
                {
                  bookId: "1",
                  quantity: 2,
                },
                null,
                2
              )}
            </pre>
          </div>
        )}
      </div>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
          <div className="w-full max-w-md p-8 bg-white shadow-2xl rounded-2xl animate-fade-in">
            <h3 className="mb-4 text-2xl font-bold text-blue-600">Added to Cart!</h3>
            <p className="text-gray-700">Book ID: <span className="font-semibold">{formData.bookId}</span></p>
            <p className="text-gray-700">Quantity: <span className="font-semibold">{formData.quantity}</span></p>
            <button
              onClick={() => setShowModal(false)}
              className="w-full px-4 py-2 mt-6 text-white transition-all bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      )}
      <ResponseDisplay response={response} error={error} />
    </div>
  );
};

export default CartForm;